import React from 'react';
import { Button } from './Button';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** 커스텀 Fallback UI (제공 시 dev 스택 자동표시는 비활성) */
  fallback?: React.ReactNode;
  /** 오류 로깅 Hook (Sentry 등) */
  onErrorCapture?: (error: unknown, info: React.ErrorInfo) => void;
  /** reset 전 callback */
  onReset?: () => void;
  /** 개발 환경에서 스택 표시 여부 강제 */
  showDevStack?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * 전역/영역별 런타임 오류를 포착하여 사용자에게 친화적인 메시지를 제공하는 Error Boundary
 * - 접근성: role="alert" / aria-live="assertive"
 * - 개발환경에서 스택 트레이스(optional)
 * - 재시도 기능 / 확장 가능한 fallback
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    if (this.props.onErrorCapture) {
      this.props.onErrorCapture(error, errorInfo);
    }
    // TODO: 외부 로깅 연동 (Sentry / Datadog 등)
    // 개발 환경 콘솔 출력
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    }
  }

  private getErrorMessage(error: Error | null): string {
    if (!error) return '알 수 없는 오류가 발생했습니다.';
    return error.message || '에러가 발생했습니다.';
  }

  reset = () => {
    if (this.props.onReset) this.props.onReset();
    this.setState({ hasError: false, error: null });
  };

  renderDevStack() {
    const { error } = this.state;
    const enableStack = this.props.showDevStack ?? import.meta.env.DEV;
    if (!enableStack || !error?.stack) return null;
    return (
      <pre className={styles.stack} data-testid="error-stack">
        {error.stack}
      </pre>
    );
  }

  render() {
    if (this.state.hasError) {
      // 커스텀 fallback 이 있으면 그대로 렌더 (dev stack은 사용자 fallback 설계에 맡김)
      if (this.props.fallback) return <>{this.props.fallback}</>;

      const message = this.getErrorMessage(this.state.error);

      return (
        <div className={styles.wrapper} role="alert" aria-live="assertive">
          <h2 className={styles.title}>문제가 발생했습니다.</h2>
          <p className={styles.message} data-testid="error-message">
            {message}
          </p>
          <div className={styles.actions}>
            <Button variant="primary" onClick={this.reset} aria-label="다시 시도">
              다시 시도
            </Button>
          </div>
          {this.renderDevStack()}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
